import analytics from '../services/analytics';
import { storageKeys } from './browser-storage/storage-keys';
import debounce from './debounce';
import emailHash from './email-hash';
import getInteractiveProductType from './interactive/get-interactive-product-type';
import extractMaterialDetails from './material/extract-material-details';
import getLabel from './material/get-label';
import formatPriceToFloat from './price/format-price-to-float';

type ProductData = {
    id: number;
    list: string;
    brand?: string;
    category?: string;
    dimension15?: string;
    dimension16?: string;
    dimension18?: string;
    dimension19?: string;
    name?: string;
    position?: number;
    price?: number;
    variant?: string;
};

type CheckoutProduct = {
    brand: string;
    // author
    category: string;
    // type of material
    dimension15: string;
    dimension16: string;
    dimension18: string;
    dimension19: string;
    id: number;
    name: string;
    price: number;
    variant: string;
    quantity?: number;
};

export type CatalogProps = {
    internalPath?: string;
    itemIndex?: number;
};

export type PaymentOptionType = {
    id: string;
    img?: string;
    title: string;
    translation?: string;
};

const getPosition = (props = {} as CatalogProps) =>
    typeof props.itemIndex === 'number' ? props.itemIndex + 1 : props.itemIndex;

export const getProductPrice = (item: Material) => {
    const currency = window.storage.getItem(storageKeys.prices.currencyCode);
    const isEUR = currency === 'EUR';
    const priceForCurrency = item?.prices && Object.values(item?.prices).find((p) => p.currency === currency);

    if (!isEUR && priceForCurrency?.currentPrice) {
        return formatPriceToFloat(priceForCurrency.currentPrice);
    }

    return formatPriceToFloat(item.sale ? item.sale.original_price : item.price) as number;
};

const productInfo = (item: Material) => {
    return {
        brand: item.author && item.author.details ? item.author.details.publicName : '',
        // author
        category: extractMaterialDetails('topSubject', item.materialTopCategories),

        // type of material
        dimension15: getLabel(item),
        dimension16: item.createdAt,
        dimension18: item.bundle ? 'bundle' : '',
        dimension19: extractMaterialDetails('classGrades', item.materialClassGrades),
        dimension22: getInteractiveProductType(item),
        id: item.id,
        name: item.title,
        price: getProductPrice(item),
        variant: extractMaterialDetails('materialTypes', item.materialTypes),
        currency: window.storage.getItem(storageKeys.prices.currencyCode) || 'EUR'
    };
};

const enhancedEcommerce = {
    tracker: {
        addToCart(arr: Material[], isBuyNow = false) {
            const products = arr.map((item) => ({
                ...productInfo(item)
            }));

            analytics.push({
                ecommerce: {
                    add: {
                        products
                    }
                },
                event: 'eeAddToCart',
                eventAction: isBuyNow ? 'buyNow' : 'addToCart',
                pinterestInfo: {
                    emailHash: emailHash.getStoredMD5()
                },
                emailSHA256: emailHash.getStoredSHA256()
            });
        },
        checkoutStep1(cartItems: CartItem[]) {
            const products: CheckoutProduct[] = [];

            cartItems.forEach((item) => {
                products.push({
                    ...productInfo(item.material),
                    quantity: item.licensesCount || 1
                });
            });

            analytics.push({
                ecommerce: {
                    checkout: {
                        actionField: { step: 1 },
                        products
                    }
                },
                event: 'eeCheckout'
            });
        },
        checkoutStep2(cartItems: CartItem[], option: string) {
            const products: CheckoutProduct[] = [];

            cartItems.forEach((item) => {
                products.push({
                    ...productInfo(item.material),
                    quantity: item.licensesCount || 1
                });
            });

            console.info({
                ecommerce: {
                    checkout: {
                        actionField: {
                            option,
                            step: 2
                        },
                        products
                    }
                },
                event: 'eeCheckout'
            });

            analytics.push({
                ecommerce: {
                    checkout: {
                        actionField: {
                            option,
                            step: 2
                        },
                        products
                    }
                },
                event: 'eeCheckout'
            });
        },
        checkoutStepComplete(option: string) {
            analytics.push({
                ecommerce: {
                    checkout_option: {
                        actionField: {
                            option,
                            step: 2
                        }
                    }
                },
                event: 'eeCheckoutStepComplete'
            });
        },
        impressionView: (() => {
            let productsInViewport: CheckoutProduct[] = [];
            const debounceImpression = debounce(trackImpression, 700);
            let impressionsHistory = {};

            function trackImpression() {
                analytics.push({
                    ecommerce: {
                        impressions: productsInViewport
                    },
                    event: 'eeImpressionView'
                });

                productsInViewport = [];
            }

            function wasImpressionTracked(product: ProductData) {
                const pageURL = window.location.href;
                const productKey = `${product.list}_${product.id}`;

                // @ts-ignore
                if (impressionsHistory[pageURL] && impressionsHistory[pageURL][productKey]) {
                    return true;
                }
            }

            function writeImpressionToHistory(product: ProductData) {
                const pageURL = window.location.href;
                const productKey = `${product.list}_${product.id}`;

                // @ts-ignore
                if (impressionsHistory[pageURL]) {
                    // @ts-ignore
                    impressionsHistory[pageURL][productKey] = true;
                } else {
                    // @ts-ignore
                    impressionsHistory[pageURL] = {
                        [productKey]: true
                    };
                }
            }

            return {
                resetImpressionsHistory() {
                    impressionsHistory = {};
                },
                track(item: Material, props: CatalogProps) {
                    const product = {
                        ...productInfo(item),
                        list: props.internalPath,
                        position: getPosition(props)
                    };

                    if (!wasImpressionTracked(product as ProductData)) {
                        productsInViewport.push(product);
                        writeImpressionToHistory(product as ProductData);
                        // @ts-ignore
                        debounceImpression();
                    }
                }
            };
        })(),
        productClick(item: Material, props: CatalogProps) {
            analytics.push({
                ecommerce: {
                    click: {
                        actionField: { list: props.internalPath },
                        products: [
                            {
                                ...productInfo(item),
                                position: getPosition(props)
                            }
                        ]
                    }
                },
                event: 'eeProductClick'
            });
        },
        productDetailsView(item: Material) {
            analytics.push({
                ecommerce: {
                    detail: {
                        products: [productInfo(item)]
                    }
                },
                event: 'eeProductDetails',
                page: {
                    type: getInteractiveProductType(item)
                }
            });
        },
        purchase(
            // eslint-disable-next-line
            orderItems: OrderItems[],
            orderDetails: OrderDetails
        ) {
            const currency = window.storage.getItem(storageKeys.prices.currencyCode);
            const products: CheckoutProduct[] = [];

            orderItems.forEach((item) => {
                products.push({
                    ...productInfo(item.material),
                    quantity: item.licensesCount || 1
                });
            });
            analytics.push({
                ecommerce: {
                    purchase: {
                        actionField: {
                            affiliation: window.location.hostname,
                            coupon: orderDetails.voucher ? orderDetails.voucher.code : '',
                            id: orderDetails.id,
                            revenue:
                                currency !== 'EUR' && orderDetails?.totalPriceInCurrency
                                    ? orderDetails.totalPriceInCurrency
                                    : orderDetails.totalPrice,
                            tax: '0'
                        },
                        products
                    }
                },
                event: 'ecommerceTransaction',
                pinterestInfo: {
                    emailHash: emailHash.getStoredMD5()
                },
                emailSHA256: emailHash.getStoredSHA256()
            });
        },
        removeFromCart(item: Material) {
            analytics.push({
                ecommerce: {
                    remove: {
                        products: [
                            {
                                ...productInfo(item)
                            }
                        ]
                    }
                },
                event: 'eeRemoveFromCart'
            });
        }
    }
};

export default enhancedEcommerce;
